import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { Psychology, Add, Search as SearchIcon } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';

interface SynonymRow {
  section_id: string;
  field_id: string;
  field_name?: string; // resolved from schema for display
  synonyms: string[];
  source: string;
}

interface SynonymManagementProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
}

export default function SynonymManagement({ configs, selectedConfigId, onSelectConfigId }: SynonymManagementProps) {
  const [rows, setRows] = useState<SynonymRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalField, setModalField] = useState<{ section_id: string; field_id: string; field_name?: string } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<number>>(new Set());
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestingAll, setSuggestingAll] = useState(false);
  const [suggestAllProgress, setSuggestAllProgress] = useState<string | null>(null);
  const [fieldNameByKey, setFieldNameByKey] = useState<Record<string, string>>({});
  const [schemaSections, setSchemaSections] = useState<Array<{ id: string; name?: string; fields?: Array<{ id?: string; name?: string }>; groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }>>([]);
  const [addManualOpen, setAddManualOpen] = useState(false);
  const [addManualSectionId, setAddManualSectionId] = useState('');
  const [addManualFieldId, setAddManualFieldId] = useState('');
  const [addManualSynonym, setAddManualSynonym] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    const lower = searchText.toLowerCase().trim();
    return rows.filter(
      (r) =>
        (r.section_id && r.section_id.toLowerCase().includes(lower)) ||
        (r.field_id && r.field_id.toLowerCase().includes(lower)) ||
        (r.field_name && r.field_name.toLowerCase().includes(lower)) ||
        r.synonyms.some((s) => s.toLowerCase().includes(lower))
    );
  }, [rows, searchText]);

  const loadSynonyms = async () => {
    if (!selectedConfigId) {
      setRows([]);
      setFieldNameByKey({});
      setSchemaSections([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [synRes, schemaRes] = await Promise.all([
        dhis2ConfigAPI.getFieldSynonyms(selectedConfigId),
        dhis2ConfigAPI.getSchema(selectedConfigId),
      ]);
      const list = Array.isArray(synRes.data) ? synRes.data : [];
      const byKey = new Map<string, SynonymRow>();
      for (const item of list) {
        const key = `${item.section_id}\t${item.field_id}`;
        if (!byKey.has(key)) {
          byKey.set(key, { section_id: item.section_id, field_id: item.field_id, synonyms: [], source: item.source });
        }
        byKey.get(key)!.synonyms.push(item.synonym);
      }
      setRows(Array.from(byKey.values()));
      const data = (schemaRes.data ?? {}) as { sections?: Array<{ id: string; name?: string; fields?: Array<{ id?: string; name?: string }>; groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }> };
      const sections = data.sections ?? [];
      setSchemaSections(sections);
      const map: Record<string, string> = {};
      for (const sec of sections) {
        const sid = sec.id ?? '';
        for (const f of sec.fields ?? []) {
          const fid = f.id ?? f.name ?? '';
          if (sid && fid) map[`${sid}\t${fid}`] = f.name ?? fid;
        }
        for (const grp of (sec as { groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }).groups ?? []) {
          for (const f of grp.fields ?? []) {
            const fid = f.id ?? f.name ?? '';
            if (sid && fid) map[`${sid}\t${fid}`] = f.name ?? fid;
          }
        }
      }
      setFieldNameByKey(map);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load synonyms');
      setRows([]);
      setFieldNameByKey({});
      setSchemaSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSynonyms();
  }, [selectedConfigId]);

  const handleSuggest = async (sectionId: string, fieldId: string, fieldName?: string) => {
    if (!selectedConfigId) return;
    setModalField({ section_id: sectionId, field_id: fieldId, field_name: fieldName });
    setModalOpen(true);
    setSuggestions([]);
    setSelectedSuggestions(new Set());
    setLoadingSuggestions(true);
    try {
      const res = await dhis2ConfigAPI.suggestSynonyms(selectedConfigId, {
        section_id: sectionId,
        field_id: fieldId,
        field_name: fieldName || fieldId,
      });
      setSuggestions(Array.isArray(res.data?.suggestions) ? res.data.suggestions : []);
    } catch (e: unknown) {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleToggleSuggestion = (index: number) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  /** Suggest synonyms for every field in the config schema, merge with existing, and save. */
  const handleSuggestAll = async () => {
    if (!selectedConfigId) return;
    setSuggestingAll(true);
    setError(null);
    setSuggestAllProgress('Loading schema…');
    try {
      const schemaRes = await dhis2ConfigAPI.getSchema(selectedConfigId);
      const schema = (schemaRes.data ?? {}) as { sections?: Array<{ id: string; name?: string; fields?: Array<{ id?: string; name?: string }>; groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }> };
      const sections = schema.sections ?? [];
      const fields: { section_id: string; field_id: string; field_name: string }[] = [];
      for (const sec of sections) {
        const sectionId = sec.id ?? '';
        for (const f of sec.fields ?? []) {
          const fieldId = f.id ?? f.name ?? '';
          if (fieldId) fields.push({ section_id: sectionId, field_id: fieldId, field_name: f.name || fieldId });
        }
        for (const grp of (sec as { groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }).groups ?? []) {
          for (const f of grp.fields ?? []) {
            const fieldId = f.id ?? f.name ?? '';
            if (fieldId) fields.push({ section_id: sectionId, field_id: fieldId, field_name: f.name || fieldId });
          }
        }
      }
      if (fields.length === 0) {
        setError('No fields in schema. Load or define schema in Form Schema or Master Screen first.');
        setSuggestAllProgress(null);
        return;
      }
      const existingRes = await dhis2ConfigAPI.getFieldSynonyms(selectedConfigId);
      const existingList = Array.isArray(existingRes.data) ? existingRes.data : [];
      const payload: Array<{ section_id: string; field_id: string; synonym: string; source: string }> = [...existingList.map((r: { section_id: string; field_id: string; synonym: string; source?: string }) => ({ section_id: r.section_id, field_id: r.field_id, synonym: r.synonym, source: r.source || 'manual' }))];
      const seen = new Set(existingList.map((r: { section_id: string; field_id: string; synonym: string }) => `${r.section_id}\t${r.field_id}\t${r.synonym}`));
      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];
        setSuggestAllProgress(`Suggesting ${i + 1}/${fields.length}: ${f.section_id}.${f.field_id}`);
        try {
          const res = await dhis2ConfigAPI.suggestSynonyms(selectedConfigId, {
            section_id: f.section_id,
            field_id: f.field_id,
            field_name: f.field_name,
          });
          const suggestionsList = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
          for (const s of suggestionsList) {
            const key = `${f.section_id}\t${f.field_id}\t${s}`;
            if (!seen.has(key)) {
              seen.add(key);
              payload.push({ section_id: f.section_id, field_id: f.field_id, synonym: s, source: 'ai_suggested' });
            }
          }
        } catch {
          // Skip this field and continue
        }
      }
      setSuggestAllProgress('Saving…');
      await dhis2ConfigAPI.putFieldSynonyms(selectedConfigId, payload);
      await loadSynonyms();
      setSuggestAllProgress(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Suggest synonyms for all failed');
      setSuggestAllProgress(null);
    } finally {
      setSuggestingAll(false);
    }
  };

  const handleAddSelected = async () => {
    if (!selectedConfigId || !modalField || selectedSuggestions.size === 0) {
      setModalOpen(false);
      return;
    }
    const toAdd = Array.from(selectedSuggestions).map((i) => suggestions[i]);
    const existing = rows.find(
      (r) => r.section_id === modalField.section_id && r.field_id === modalField.field_id
    );
    const currentList = Array.isArray(existing?.synonyms) ? existing.synonyms : [];
    const newList = [...currentList, ...toAdd];
    const fullPayload = rows
      .filter((r) => !(r.section_id === modalField.section_id && r.field_id === modalField.field_id))
      .flatMap((r) => r.synonyms.map((s) => ({ section_id: r.section_id, field_id: r.field_id, synonym: s, source: r.source })));
    for (const s of newList) {
      fullPayload.push({ section_id: modalField.section_id, field_id: modalField.field_id, synonym: s, source: 'manual' });
    }
    try {
      await dhis2ConfigAPI.putFieldSynonyms(selectedConfigId, fullPayload);
      await loadSynonyms();
    } catch {
      // ignore
    }
    setModalOpen(false);
  };

  const buildFlatSynonymsPayload = (): Array<{ section_id: string; field_id: string; synonym: string; source: string }> => {
    return rows.flatMap((r) =>
      r.synonyms.map((s) => ({ section_id: r.section_id, field_id: r.field_id, synonym: s, source: r.source || 'manual' }))
    );
  };

  const handleAddManualSubmit = async () => {
    if (!selectedConfigId || !addManualSynonym.trim()) return;
    const sectionId = addManualSectionId.trim();
    const fieldId = addManualFieldId.trim();
    if (!sectionId || !fieldId) return;
    setSavingManual(true);
    setError(null);
    try {
      const fullPayload = buildFlatSynonymsPayload();
      fullPayload.push({
        section_id: sectionId,
        field_id: fieldId,
        synonym: addManualSynonym.trim(),
        source: 'manual',
      });
      await dhis2ConfigAPI.putFieldSynonyms(selectedConfigId, fullPayload);
      await loadSynonyms();
      setAddManualOpen(false);
      setAddManualSectionId('');
      setAddManualFieldId('');
      setAddManualSynonym('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add synonym');
    } finally {
      setSavingManual(false);
    }
  };

  const fieldsForManualSection = useMemo(() => {
    if (!addManualSectionId) return [];
    const sec = schemaSections.find((s) => s.id === addManualSectionId);
    if (!sec) return [];
    const standalone = sec.fields ?? [];
    const fromGroups = ((sec as { groups?: Array<{ fields?: Array<{ id?: string; name?: string }> }> }).groups ?? []).flatMap((g) => g.fields ?? []);
    return [...standalone, ...fromGroups];
  }, [addManualSectionId, schemaSections]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Psychology />}
          onClick={handleSuggestAll}
          disabled={!selectedConfigId || suggestingAll}
        >
          {suggestingAll ? (suggestAllProgress ?? 'Running…') : 'Suggest synonyms for all'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => setAddManualOpen(true)}
          disabled={!selectedConfigId}
        >
          Add synonym
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to manage field synonyms.</Typography>
      ) : loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Box>
          <TextField
            size="small"
            placeholder="Search by section, field ID, field name, or synonym…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
            sx={{ mb: 1.5, maxWidth: 400 }}
            fullWidth
          />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Section</TableCell>
                  <TableCell>Field ID</TableCell>
                  <TableCell>Field Name</TableCell>
                  <TableCell>Synonyms</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      {rows.length === 0
                        ? 'No synonyms yet. Add fields from Schema Editor or Master Screen, then use Suggest AI.'
                        : 'No rows match the search.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((r) => (
                    <TableRow key={`${r.section_id}-${r.field_id}`}>
                      <TableCell>{r.section_id}</TableCell>
                      <TableCell>{r.field_id}</TableCell>
                      <TableCell>{fieldNameByKey[`${r.section_id}\t${r.field_id}`] ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>{r.synonyms.join(', ') || '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<Psychology />} onClick={() => handleSuggest(r.section_id, r.field_id, fieldNameByKey[`${r.section_id}\t${r.field_id}`])}>
                          Suggest AI
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog open={addManualOpen} onClose={() => setAddManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add synonym manually</DialogTitle>
        <DialogContent>
          <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1 }}>
            <InputLabel>Section</InputLabel>
            <Select
              value={addManualSectionId}
              label="Section"
              onChange={(e) => {
                setAddManualSectionId(e.target.value);
                setAddManualFieldId('');
              }}
            >
              <MenuItem value="">— Select section —</MenuItem>
              {schemaSections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name ?? s.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={{ mb: 1 }}>
            <InputLabel>Field</InputLabel>
            <Select
              value={addManualFieldId}
              label="Field"
              onChange={(e) => setAddManualFieldId(e.target.value)}
              disabled={!addManualSectionId}
            >
              <MenuItem value="">— Select field —</MenuItem>
              {fieldsForManualSection.map((f) => {
                const fieldKey = f.id ?? f.name ?? '';
                if (!fieldKey) return null;
                return (
                <MenuItem key={fieldKey} value={fieldKey}>
                  {f.name ?? fieldKey}
                </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            label="Synonym"
            value={addManualSynonym}
            onChange={(e) => setAddManualSynonym(e.target.value)}
            placeholder="e.g. Hb, Hgb"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddManualOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddManualSubmit}
            disabled={!addManualSectionId || !addManualFieldId || !addManualSynonym.trim() || savingManual}
          >
            {savingManual ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suggest synonyms for {modalField ? `${modalField.section_id}.${modalField.field_id}` : ''}</DialogTitle>
        <DialogContent>
          {loadingSuggestions ? (
            <Typography>Loading suggestions…</Typography>
          ) : suggestions.length === 0 ? (
            <Typography color="text.secondary">No suggestions returned. Check LLM configuration.</Typography>
          ) : (
            <FormGroup sx={{ pt: 1 }}>
              {suggestions.map((s, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={selectedSuggestions.has(i)}
                      onChange={() => handleToggleSuggestion(i)}
                    />
                  }
                  label={s}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSelected} disabled={selectedSuggestions.size === 0}>
            Add selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
